import AddCriterionDialog from 'components/Rubrics/AddCriterionDialog';
import EditCriterionDialog from 'components/Rubrics/EditCriterionDialog';
import { useViewport } from 'contexts/viewport';
import { cloneDeep, omit } from 'lodash';
import { memo, useCallback, useEffect, useState } from 'react';
import { RubricTable, Stack, TextInput, styled } from 'shell/components';
import { insertAtIndex } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const EditRubricForm = memo(
    ({ form, setForm, errors, setErrors, hasClickedSubmit, validateTitle, validateCriteria, ...rest }) => {
        const { device } = useViewport();

        const [createCriterionDialog, setCreateCriterionDialog] = useState({ open: false });
        const [editCriterionDialog, setEditCriterionDialog] = useState({ open: false });

        const addCriterion = (title, description) => {
            setForm((current) => {
                return {
                    ...current,
                    criteria: [
                        ...current.criteria,
                        {
                            id: uuid(),
                            title,
                            description,
                            options: [
                                {
                                    id: uuid(),
                                    title: '',
                                    description: '',
                                    rate: '',
                                },
                                {
                                    id: uuid(),
                                    title: '',
                                    description: '',
                                    rate: '',
                                },
                            ],
                        },
                    ],
                };
            });
            setCreateCriterionDialog({ open: false });
        };

        const updateCriterion = (criterionId, title, description) => {
            setForm((current) => ({
                ...current,
                criteria: current.criteria.map((criterion) => {
                    if (criterion.id !== criterionId) return criterion;
                    return {
                        ...criterion,
                        title,
                        description,
                    };
                }),
            }));
            setEditCriterionDialog((current) => ({ ...current, open: false }));
        };

        const deleteCriterion = (criterionId) => {
            setForm((current) => ({
                ...current,
                criteria: current.criteria.filter(({ id }) => id !== criterionId),
            }));
            setErrors((current) => ({ ...current, criteria: omit(current.criteria, [criterionId]) }));
            setEditCriterionDialog((current) => ({ ...current, open: false }));
        };

        const handleSort = useCallback(
            (value) => {
                setForm((current) => {
                    const newCriteria = cloneDeep(current.criteria).map((criterion) => {
                        criterion.options.sort((a, b) => {
                            const numA = Number(a.rate);
                            const numB = Number(b.rate);
                            if (!numA && !numB) return 0;
                            if (a.rate === '') return 1;
                            if (b.rate === '') return -1;
                            if (value === 'asc') return numA - numB;
                            return numB - numA;
                        });
                        return criterion;
                    });
                    return { ...current, criteria: newCriteria, sort: value };
                });
            },
            [setForm]
        );

        const handleChange = useCallback(
            (criterionId, optionId, key, value) => {
                setForm((current) => {
                    return {
                        ...current,
                        criteria: current.criteria.map((criterion) => {
                            if (criterion.id !== criterionId) return criterion;
                            return {
                                ...criterion,
                                options: criterion.options.map((option) => {
                                    if (option.id !== optionId) return option;
                                    return {
                                        ...option,
                                        [key]: value,
                                    };
                                }),
                            };
                        }),
                    };
                });
            },
            [setForm]
        );

        const handleClickAddCriterion = useCallback(() => {
            setCreateCriterionDialog({ open: true });
        }, []);

        const handleClickEditCriterion = useCallback(
            (criterionId) => {
                const { id, title, description } = form.criteria.find(({ id }) => id === criterionId);
                setEditCriterionDialog({ open: true, id, title, description });
            },
            [form.criteria]
        );

        const handleClickDuplicateCriterion = useCallback(
            (criterionId) => {
                const criterionToDuplicate = form.criteria.find(({ id }) => id === criterionId);
                setForm((current) => {
                    return {
                        ...current,
                        criteria: [
                            ...current.criteria,
                            {
                                ...criterionToDuplicate,
                                id: uuid(),
                                options: criterionToDuplicate.options.map((option) => ({ ...option, id: uuid() })),
                            },
                        ],
                    };
                });
            },
            [form.criteria, setForm]
        );

        const handleClickAddOption = useCallback(
            (criterionId, index) => {
                setForm((current) => {
                    return {
                        ...current,
                        criteria: current.criteria.map((criterion) => {
                            if (criterion.id !== criterionId) return criterion;
                            if (criterion.options.length === index) {
                                const el = document.getElementById(`${criterion.id}-stack`);
                                setTimeout(() => {
                                    el.scrollLeft = el.scrollWidth;
                                }, 0);
                            }
                            return {
                                ...criterion,
                                options: insertAtIndex(criterion.options, index, {
                                    id: uuid(),
                                    title: '',
                                    description: '',
                                    rate: '',
                                }),
                            };
                        }),
                    };
                });
            },
            [setForm]
        );

        const handleClickDeleteOption = useCallback(
            (criterionId, optionId) => {
                setForm((current) => {
                    return {
                        ...current,
                        criteria: current.criteria.map((criterion) => {
                            if (criterion.id !== criterionId) return criterion;
                            return {
                                ...criterion,
                                options: criterion.options.filter(({ id }) => id !== optionId),
                            };
                        }),
                    };
                });
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current.criteria || {}),
                        [criterionId]: omit(current.criteria?.[criterionId], [optionId]),
                    },
                }));
            },
            [setErrors, setForm]
        );

        const handleDragEnd = useCallback(
            ({ draggableId, source, destination }) => {
                if (!destination) return;
                const srcIndex = source.index;
                const dstIndex = destination.index;
                if (srcIndex === dstIndex) return;
                setForm((current) => {
                    const newCriteria = cloneDeep(current.criteria);
                    newCriteria.splice(srcIndex, 1);
                    newCriteria.splice(
                        dstIndex,
                        0,
                        current.criteria.find(({ id }) => id === draggableId)
                    );
                    return {
                        ...current,
                        criteria: newCriteria,
                    };
                });
            },
            [setForm]
        );

        useEffect(() => {
            if (hasClickedSubmit) validateTitle();
        }, [hasClickedSubmit, validateTitle]);

        useEffect(() => {
            if (hasClickedSubmit) validateCriteria();
        }, [hasClickedSubmit, validateCriteria]);

        useEffect(() => {
            if (errors.title) {
                document.getElementById('rubric-name-input').scrollIntoView(false);
            } else {
                const optionErrors = Object.entries(errors.criteria)
                    .map(([_, value]) => {
                        return Object.entries(value).filter(([_, { rate }]) => {
                            return Boolean(rate);
                        });
                    })
                    .flat();
                if (optionErrors.length) {
                    const optionId = optionErrors[0][0];
                    const el = document.getElementById(optionId);
                    el?.scrollIntoView(false);
                }
            }
        }, [errors]);

        return (
            <>
                <RootStack spacing={5} {...rest}>
                    <CustomTextInput
                        id={'rubric-name-input'}
                        device={device}
                        error={Boolean(errors.title)}
                        helperText={errors.title}
                        label={'Rubric Name'}
                        placeholder={'Enter rubric name'}
                        required
                        value={form.title}
                        onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                    />
                    <RubricTable
                        type={'create'}
                        data={form.criteria}
                        errors={errors.criteria}
                        sort={form.sort}
                        setSort={handleSort}
                        onChange={handleChange}
                        onClickAddCriterion={handleClickAddCriterion}
                        onClickEditCriterion={handleClickEditCriterion}
                        onClickDuplicateCriterion={handleClickDuplicateCriterion}
                        onClickAddOption={handleClickAddOption}
                        onClickDeleteOption={handleClickDeleteOption}
                        onDragEnd={handleDragEnd}
                    />
                </RootStack>
                <AddCriterionDialog
                    open={createCriterionDialog.open}
                    onClose={() => setCreateCriterionDialog({ open: false })}
                    onSave={addCriterion}
                />
                <EditCriterionDialog
                    open={editCriterionDialog.open}
                    criterion={{
                        id: editCriterionDialog.id,
                        title: editCriterionDialog.title,
                        description: editCriterionDialog.description,
                    }}
                    onClose={() => setEditCriterionDialog((current) => ({ ...current, open: false }))}
                    onSave={updateCriterion}
                    onDelete={deleteCriterion}
                />
            </>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(({ device }) => {
    return {
        ...(device !== 'mobile' && {
            maxWidth: '300px',
        }),
    };
});

export default EditRubricForm;
